import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  useMemo,
} from "react";
import { connect, useSelector } from "react-redux";
import _isUndefined from "lodash/isUndefined";
import _get from "lodash/get";
import _isEmpty from "lodash/isEmpty";

import useDebounce from "../../pages/Notes/NoteCreate/InputWithMention/useDebounce";
import { getDataComponentFlattenedRequestState } from "../../../reducers/dataComponentReducer";
import {
  initDataComponent,
  performRetrieveListRequest,
  destroyDataComponentResource,
  setPageNumberAction,
} from "../../../actions/dataComponentActions";
import * as RequestTypes from "../../../constants/RequestTypes";
import useScope from "../../hooks/useScope";
import Scope from "../../../models/Scope";
import { getBluechipResources } from "../../../utils/bluechipUtils";
import { LAZY_LOAD_PAGE_SIZE } from "../../../constants/lazyLoad";

const useInitDataComponent = (
  APIOptions,
  dataComponentId,
  initDataComponent,
  destroyDataComponentResource
) => () => {
  const { model, route, includes = [], omitDestroyDataComponent } = APIOptions;
  initDataComponent(dataComponentId, model, includes, route, true, "v2");
  return () =>
    omitDestroyDataComponent
      ? null
      : destroyDataComponentResource(dataComponentId, RequestTypes.LIST);
};

export const getValueIds = value => {
  return Array.isArray(value)
    ? value.map(currentValue => _get(currentValue, "id", currentValue))
    : _get(value, "id", value);
};

const getScopeFilter = (APIOptions, scopeId) => {
  return scopeId &&
    Scope.filterScopeModels.includes(APIOptions.model.name) &&
    !APIOptions.omitFilterScopeModels
    ? { "scope.id": scopeId }
    : {};
};

const handleFetchItems = (
  APIOptions,
  dataComponentId,
  performRetrieveListRequest,
  value,
  pageSize
) => () => {
  if (APIOptions.fetchOnlyOnce) return;
  if (value && value !== "-1" && !["undefined", "null"].includes(value)) {
    const mapIds = getValueIds(value);
    performRetrieveListRequest(dataComponentId, {
      pageSize: pageSize,
      ...APIOptions,
      rootFilters: {
        $where: {
          id: Array.isArray(mapIds) ? { $in: mapIds } : mapIds,
        },
      },
    });
  }
};

const preventReload = ({ APIOptions, debounceQuery, query, lazyLoad }) =>
  _isUndefined(APIOptions) ||
  APIOptions.fetchOnlyOnce ||
  _isUndefined(debounceQuery) ||
  debounceQuery !== query ||
  (lazyLoad && !debounceQuery);

const reFetch = (shouldFetch, reloadDataComponent) => {
  if (shouldFetch) {
    reloadDataComponent("");
  }
};

const getLabelProperty = (labelProperty, apiFilterLabelProperty) =>
  typeof labelProperty === "function" ? apiFilterLabelProperty : labelProperty;

function useLazyHandlers({
  lazyLoad,
  resetLazyState,
  doInitialLazyFetch,
  setQuery,
  onChange,
  onMenuOpen,
  APIOptions,
  reloadDataComponent,
}) {
  const hasLoadedListRef = useRef(false);
  const wasSearchingRef = useRef(false);
  // Tracks whether the currently loaded list reflects a search filter rather
  // than the full list. Selecting an option doesn't re-fetch (to avoid a
  // network call on selection), so the next menu open is responsible for
  // restoring the full ordered list when this is set.
  const hasFilteredRef = useRef(false);

  useEffect(() => {
    if (!lazyLoad) return;
    resetLazyState();
    doInitialLazyFetch();
    hasLoadedListRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleInputChange = useCallback(
    (query, actionMeta) => {
      if (lazyLoad) {
        if (actionMeta?.action === "set-value") {
          wasSearchingRef.current = false;
          setQuery(query);
          return;
        }
        if (query) {
          wasSearchingRef.current = true;
          hasFilteredRef.current = true;
          resetLazyState();
        } else if (wasSearchingRef.current) {
          wasSearchingRef.current = false;
          hasFilteredRef.current = false;
          resetLazyState({ clear: true });
          doInitialLazyFetch();
          setQuery(query);
          return;
        }
      }
      setQuery(query);
    },
    [doInitialLazyFetch, lazyLoad, resetLazyState, setQuery]
  );

  const handleChange = useCallback(
    event => {
      onChange(event);
      if (_isEmpty(event.target.value)) {
        if (lazyLoad) resetLazyState();
        reFetch(!APIOptions.fetchOnlyOnce, reloadDataComponent);
      }
    },
    [
      APIOptions.fetchOnlyOnce,
      lazyLoad,
      onChange,
      reloadDataComponent,
      resetLazyState,
    ]
  );

  const handleMenuOpen = useCallback(
    event => {
      onMenuOpen && onMenuOpen(event);
      if (lazyLoad) {
        // Refetch the full ordered list on first open, or whenever the list is
        // still showing a previous search (e.g. the user searched and then
        // selected an option). This keeps reopening from staying stuck on the
        // old search and restores scroll autoloading.
        if (!hasLoadedListRef.current || hasFilteredRef.current) {
          resetLazyState({ clear: hasFilteredRef.current });
          doInitialLazyFetch();
          hasLoadedListRef.current = true;
          hasFilteredRef.current = false;
        }
        return;
      }
      reFetch(!APIOptions.fetchOnlyOnce, reloadDataComponent);
    },
    [
      APIOptions.fetchOnlyOnce,
      doInitialLazyFetch,
      lazyLoad,
      onMenuOpen,
      reloadDataComponent,
      resetLazyState,
    ]
  );

  return { handleInputChange, handleChange, handleMenuOpen };
}

export const getDynamicTopLevelFilter = (APIOptions, value) => {
  const { dynamicFilterKey } = APIOptions;
  if (dynamicFilterKey && value && value !== "-1") {
    return { [dynamicFilterKey]: value };
  }
  return {};
};

const getSelectedId = value =>
  value && typeof value === "object" ? value.id : value;

const isSearchPending = (APIOptions, lazyLoad, query, debounceQuery) =>
  !APIOptions.fetchOnlyOnce && !lazyLoad && query !== debounceQuery;

// Pick the option to keep displayed for the current value. Prefer the
// remembered option (tracks the latest selection), then resolvedInitialOption
// (fetched once on mount, so only valid for the initial value). With no current
// value, fall back to resolvedInitialOption (e.g. an explicit initialSelectedOption).
const getPinnedOption = (
  selectedId,
  rememberedOption,
  resolvedInitialOption,
  idKey
) => {
  const matches = option => option && option[idKey] === selectedId;
  if (!selectedId) return resolvedInitialOption || null;
  if (matches(rememberedOption)) return rememberedOption;
  if (matches(resolvedInitialOption)) return resolvedInitialOption;
  return null;
};

const fetchSelectedItem = (
  initSelectedDCId,
  APIOptions,
  value,
  initDataComponent,
  performRetrieveListRequest
) => {
  const selectedId = getSelectedId(value);
  if (!selectedId || selectedId === "-1") return false;
  const { model, route, includes = [], fields } = APIOptions;
  initDataComponent(initSelectedDCId, model, includes, route, true, "v2");
  performRetrieveListRequest(initSelectedDCId, {
    model,
    route,
    pageSize: 1,
    rootFilters: { $where: { id: selectedId } },
    ...(fields && { fields }),
    sort: [],
  });
  return true;
};

const canLoadNextPage = (dataComponent, lazyLoad, isResettingRef) => {
  if (!lazyLoad || dataComponent.loading || isResettingRef.current)
    return false;
  const totalPages = dataComponent.totalPages || 1;
  return (dataComponent.pageNumber || 0) < totalPages - 1;
};

const applyPageResources = (
  isResettingRef,
  currentPageResources,
  setAccumulatedOptions
) => {
  if (isResettingRef.current) {
    isResettingRef.current = false;
    setAccumulatedOptions([...currentPageResources]);
  } else {
    setAccumulatedOptions(prev => [...prev, ...currentPageResources]);
  }
};

function useLazyLoad({
  lazyLoad,
  currentPageResources,
  dataComponent,
  dataComponentId,
  debounceQuery,
  reloadDataComponent,
  setPageNumberAction,
}) {
  const [accumulatedOptions, setAccumulatedOptions] = useState([]);
  const isResettingRef = useRef(false);
  const prevLoadingRef = useRef(false);

  const resetLazyState = useCallback(
    ({ clear = false } = {}) => {
      isResettingRef.current = true;
      // For a full reload (reopen after a search, or clearing the search), drop
      // the stale options right away so the menu shows the loader instead of the
      // previous results until the fresh list arrives. The currently selected
      // option stays displayed via resolvedInitialOption. Per-keystroke searches
      // pass clear=false to keep the list visible while typing.
      if (clear) setAccumulatedOptions([]);
      setPageNumberAction(dataComponentId, 0);
    },
    [dataComponentId, setPageNumberAction]
  );

  useEffect(() => {
    if (!lazyLoad) return;
    const isNowLoading = !!dataComponent.loading;
    const justFinishedLoading = prevLoadingRef.current && !isNowLoading;
    prevLoadingRef.current = isNowLoading;
    if (justFinishedLoading) {
      applyPageResources(
        isResettingRef,
        currentPageResources,
        setAccumulatedOptions
      );
    }
  }, [lazyLoad, dataComponent.loading, currentPageResources]);

  const handleScrollNearBottom = useCallback(() => {
    if (!canLoadNextPage(dataComponent, lazyLoad, isResettingRef)) return;
    const nextPage = (dataComponent.pageNumber || 0) + 1;
    setPageNumberAction(dataComponentId, nextPage);
    reloadDataComponent(debounceQuery);
  }, [
    dataComponent,
    dataComponentId,
    debounceQuery,
    lazyLoad,
    reloadDataComponent,
    setPageNumberAction,
  ]);

  return { accumulatedOptions, resetLazyState, handleScrollNearBottom };
}

// eslint-disable-next-line max-lines-per-function
function withAPIFilter(WrappedComponent, pageSize = 10) {
  // eslint-disable-next-line max-lines-per-function
  const APIFilter = ({
    dataComponent = {},
    currentPageResources = [],
    performRetrieveListRequest,
    setPageNumberAction,
    initDataComponent,
    destroyDataComponentResource,
    APIOptions,
    apiFilterLabelProperty,
    lazyLoad,
    office,
    onChange,
    onMenuOpen,
    initialSelectedOption,
    ...props
    // eslint-disable-next-line max-lines-per-function, sonarjs/cognitive-complexity
  }) => {
    const { dataComponentId, value } = props;
    const labelProperty = getLabelProperty(
      props.labelProperty,
      apiFilterLabelProperty
    );
    const [query, setQuery] = useState("");
    const debounceQuery = useDebounce(query, 700);

    // A server-side search is pending while the user keeps typing and the
    // debounced query hasn't caught up yet. During that window we want to
    // suppress the previously loaded list instead of showing stale results.
    const searchPending = isSearchPending(
      APIOptions,
      lazyLoad,
      query,
      debounceQuery
    );

    const effectivePageSize = lazyLoad ? LAZY_LOAD_PAGE_SIZE : pageSize;
    const additionalFilter = useMemo(
      () => _get(APIOptions, "rootFilters.$where", {}),
      [APIOptions]
    );
    const dynamicTopLevelFilter = useMemo(
      () => getDynamicTopLevelFilter(APIOptions, value),
      [APIOptions, value]
    );
    const additionalSort = useMemo(() => _get(APIOptions, "sort", []), [
      APIOptions,
    ]);
    const scope = useScope();

    const scopeFilter = useMemo(
      () => getScopeFilter(APIOptions, scope?.id || office?.scopeId),
      [APIOptions, office, scope]
    );

    const reloadDataComponent = useCallback(
      query => {
        performRetrieveListRequest(dataComponentId, {
          pageSize: effectivePageSize,
          ...APIOptions,
          rootFilters: {
            ...dynamicTopLevelFilter,
            $where: {
              [labelProperty]: {
                $ilike: `%${query}%`,
              },
              ...additionalFilter,
              ...scopeFilter,
            },
          },
          sort: additionalSort,
        });
      },
      [
        APIOptions,
        additionalFilter,
        additionalSort,
        dataComponentId,
        dynamicTopLevelFilter,
        effectivePageSize,
        labelProperty,
        performRetrieveListRequest,
        scopeFilter,
      ]
    );

    useEffect(
      useInitDataComponent(
        APIOptions,
        dataComponentId,
        initDataComponent,
        destroyDataComponentResource
      ),
      [APIOptions, dataComponentId, initDataComponent]
    );

    useEffect(
      handleFetchItems(
        APIOptions,
        dataComponentId,
        performRetrieveListRequest,
        lazyLoad ? null : value,
        effectivePageSize
      ),
      [
        APIOptions,
        dataComponentId,
        effectivePageSize,
        lazyLoad,
        performRetrieveListRequest,
        value,
      ]
    );

    useEffect(() => {
      if (!APIOptions.fetchOnlyOnce || fetchOnceExecutedRef.current) return;
      fetchOnceExecutedRef.current = true;
      reloadDataComponent("");
    }, [APIOptions.fetchOnlyOnce, reloadDataComponent]);

    useEffect(() => {
      if (preventReload({ APIOptions, debounceQuery, query, lazyLoad })) return;
      reloadDataComponent(debounceQuery);
    }, [
      APIOptions,
      debounceQuery,
      lazyLoad,
      query,
      reloadDataComponent,
      value,
    ]);

    const {
      accumulatedOptions,
      resetLazyState,
      handleScrollNearBottom,
    } = useLazyLoad({
      lazyLoad,
      currentPageResources,
      dataComponent,
      dataComponentId,
      debounceQuery,
      reloadDataComponent,
      setPageNumberAction,
    });

    const fetchOnceExecutedRef = useRef(false);
    const hasFetchedSelectedRef = useRef(false);

    const initSelectedDCId = `${dataComponentId}-selected-init`;

    const fetchedInitialOption = useSelector(state => {
      if (!state.dataComponents) return null;
      const dcState = getDataComponentFlattenedRequestState(
        initSelectedDCId,
        state
      );
      return (getBluechipResources(dcState, state) || [])[0] || null;
    });

    const resolvedInitialOption = initialSelectedOption || fetchedInitialOption;

    useEffect(() => {
      if (!lazyLoad || initialSelectedOption || hasFetchedSelectedRef.current)
        return;
      hasFetchedSelectedRef.current = true;
      const didFetch = fetchSelectedItem(
        initSelectedDCId,
        APIOptions,
        value,
        initDataComponent,
        performRetrieveListRequest
      );
      if (!didFetch) return;
      return () =>
        destroyDataComponentResource(initSelectedDCId, RequestTypes.LIST);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const doInitialLazyFetch = useCallback(() => {
      performRetrieveListRequest(dataComponentId, {
        pageSize: effectivePageSize,
        ...APIOptions,
        rootFilters: {
          ...dynamicTopLevelFilter,
          $where: {
            [labelProperty]: { $ilike: "%%" },
            ...additionalFilter,
            ...scopeFilter,
          },
        },
        sort: additionalSort,
      });
    }, [
      APIOptions,
      additionalFilter,
      additionalSort,
      dataComponentId,
      dynamicTopLevelFilter,
      effectivePageSize,
      labelProperty,
      performRetrieveListRequest,
      scopeFilter,
    ]);

    const { handleInputChange, handleChange, handleMenuOpen } = useLazyHandlers(
      {
        lazyLoad,
        resetLazyState,
        doInitialLazyFetch,
        setQuery,
        onChange,
        onMenuOpen,
        APIOptions,
        reloadDataComponent,
      }
    );

    // Remember the selected option object whenever it shows up in the loaded
    // data (initial page or a search result). This survives list reloads so the
    // chosen value keeps displaying even when it's not in the freshly loaded
    // page (e.g. reopening the menu after a search).
    const idKey = props.valueProperty || "id";
    const selectedId = getSelectedId(value);
    const selectedOptionRef = useRef(null);
    useEffect(() => {
      if (!selectedId) {
        selectedOptionRef.current = null;
        return;
      }
      const found = accumulatedOptions.find(
        option => option[idKey] === selectedId
      );
      if (found) selectedOptionRef.current = found;
    }, [accumulatedOptions, selectedId, idKey]);

    // Pin the option matching the CURRENT value so it stays displayed across
    // list reloads (see getPinnedOption).
    const pinnedOption = getPinnedOption(
      selectedId,
      selectedOptionRef.current,
      resolvedInitialOption,
      idKey
    );

    const lazyOptions = useMemo(() => {
      if (!lazyLoad || !pinnedOption) return accumulatedOptions;
      const isIncluded = accumulatedOptions.some(
        o => o[idKey] === pinnedOption[idKey]
      );
      return isIncluded
        ? accumulatedOptions
        : [pinnedOption, ...accumulatedOptions];
    }, [accumulatedOptions, pinnedOption, lazyLoad, idKey]);

    return (
      <WrappedComponent
        {...props}
        onMenuOpen={handleMenuOpen}
        onInputChange={handleInputChange}
        onChange={handleChange}
        isLoading={!!dataComponent.loading}
        searchPending={searchPending}
        {...(lazyLoad && {
          options: lazyOptions,
          onScrollNearBottom: handleScrollNearBottom,
          filterOption: () => true,
        })}
      />
    );
  };

  const mapDispatchToProps = {
    initDataComponent,
    performRetrieveListRequest,
    destroyDataComponentResource,
    setPageNumberAction,
  };

  const mapStateToProps = (state, { dataComponentId, lazyLoad }) => {
    const dataComponent = getDataComponentFlattenedRequestState(
      dataComponentId,
      state
    );
    return {
      dataComponent,
      office: state.auth?.office,
      ...(lazyLoad && {
        currentPageResources: getBluechipResources(dataComponent, state) || [],
      }),
    };
  };

  const ConnectedComponent = connect(
    mapStateToProps,
    mapDispatchToProps
  )(APIFilter);

  const Wrapper = ({ APIOptions, ...props }) =>
    APIOptions ? (
      <ConnectedComponent APIOptions={APIOptions} {...props} />
    ) : (
      <WrappedComponent {...props} />
    );

  return Wrapper;
}

export default withAPIFilter;
